import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, ChevronsDown } from "lucide-react";

/**
 * ChapterDropdown — same visual treatment as Chapter, but the header is a
 * toggle that expands/collapses its children.
 *
 * Default state: collapsed (so the page loads compact — only the cover +
 * chapter headers are visible).
 */
/**
 * Brand palette (from the pin logo):
 *   - Dark teal/blue base: ~#0A3D54 (close to Tailwind sky-900 / cyan-900)
 *   - Mid teal highlight:  ~#1F7A9A (close to Tailwind cyan-700)
 *   - Lime pop accent:     ~#B8D936 (close to Tailwind lime-400)
 *
 * All 4 chapter dropdowns now share the same brand-dark-blue background.
 * Each chapter gets a different SECONDARY accent color so they remain
 * individually distinguishable while staying on-brand.
 */
const ACCENTS = {
  emerald: {
    border: "border-lime-400/40",
    kicker: "text-lime-300",
    number: "text-lime-400",
    chevron: "text-lime-300",
    glow:   "bg-[radial-gradient(ellipse_at_top_right,rgba(163,230,53,0.20),transparent_70%)]",
    pillBg: "bg-lime-400/15", pillBorder: "border-lime-400/40", pillText: "text-lime-200",
  },
  blue: {
    border: "border-cyan-400/35",
    kicker: "text-cyan-200",
    number: "text-cyan-300",
    chevron: "text-cyan-300",
    glow:   "bg-[radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.18),transparent_70%)]",
    pillBg: "bg-cyan-500/15", pillBorder: "border-cyan-400/30", pillText: "text-cyan-100",
  },
  violet: {
    border: "border-cyan-400/35",
    kicker: "text-cyan-200",
    number: "text-cyan-300",
    chevron: "text-cyan-300",
    glow:   "bg-[radial-gradient(ellipse_at_top_right,rgba(34,211,238,0.18),transparent_70%)]",
    pillBg: "bg-cyan-500/15", pillBorder: "border-cyan-400/30", pillText: "text-cyan-100",
  },
  amber: {
    border: "border-teal-400/35",
    kicker: "text-teal-200",
    number: "text-teal-300",
    chevron: "text-teal-300",
    glow:   "bg-[radial-gradient(ellipse_at_top_right,rgba(45,212,191,0.18),transparent_70%)]",
    pillBg: "bg-teal-500/15", pillBorder: "border-teal-400/30", pillText: "text-teal-100",
  },
  indigo: {
    border: "border-sky-300/40",
    kicker: "text-sky-200",
    number: "text-sky-200",
    chevron: "text-sky-200",
    glow:   "bg-[radial-gradient(ellipse_at_top_right,rgba(125,211,252,0.22),transparent_70%)]",
    pillBg: "bg-sky-400/15", pillBorder: "border-sky-300/35", pillText: "text-sky-100",
  },
};

export const ChapterDropdown = ({
  id,
  number,
  kicker,
  title,
  prompt,
  accent = "emerald",
  sectionCount,
  defaultOpen = false,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const a = ACCENTS[accent] || ACCENTS.emerald;

  return (
    <div
      id={id}
      className="relative z-10 mt-10 mb-6 scroll-mt-4"
      data-testid={`chapter-${id}`}
    >
      <div className={`relative overflow-hidden rounded-3xl border ${a.border} bg-[#0a3d54]`}>
        {/* Corner glow */}
        <div className={`absolute top-0 right-0 w-[480px] h-[260px] ${a.glow} pointer-events-none`} />

        {/* Clickable header */}
        <div
          onClick={() => setOpen((v) => !v)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setOpen((v) => !v);
            }
          }}
          role="button"
          tabIndex={0}
          aria-expanded={open}
          data-testid={`chapter-${id}-toggle`}
          className="relative px-4 md:px-10 py-5 md:py-8 flex items-center gap-4 md:gap-7 cursor-pointer select-none group"
        >
          {number && (
            <div
              className={`text-4xl md:text-7xl font-black leading-none tabular-nums ${a.number} select-none flex-shrink-0`}
            >
              {number}
            </div>
          )}
          <div className="flex-1 min-w-0">
            {kicker && (
              <p className={`${a.kicker} text-[11px] md:text-xs font-bold uppercase tracking-[0.2em] md:tracking-[0.25em] mb-1.5`}>
                {kicker}
              </p>
            )}
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <h2 className="text-xl md:text-4xl font-black text-white leading-tight">{title}</h2>
              {sectionCount && (
                <span className={`${a.pillText} text-xs ${a.pillBg} border ${a.pillBorder} rounded-full px-2.5 py-0.5 inline-flex items-center gap-1 font-semibold`}>
                  <ChevronsDown className="w-3 h-3" />
                  {sectionCount}
                </span>
              )}
            </div>
            {prompt && (
              <p className="text-slate-300 text-xs md:text-base mt-1.5 max-w-3xl leading-relaxed">
                {prompt}
              </p>
            )}
            <p className={`${a.chevron} text-[11px] mt-2 opacity-80 group-hover:opacity-100 transition-opacity`}>
              {open ? "Click to collapse" : "Click to expand"}
            </p>
          </div>
          <ChevronDown
            className={`w-6 h-6 md:w-7 md:h-7 ${a.chevron} flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
          />
        </div>

        {/* Body */}
        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="chapter-body"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="px-3 md:px-6 pb-6 pt-2">
                {children}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
